/**
 * Dashboard Layout
 *
 * Main application shell with fixed sidebar navigation.
 * Features refined typography, Lucide icons, and subtle interactions.
 */

import { useMemo } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  LayoutDashboard,
  Users,
  UserPlus,
  FileText,
  CheckSquare,
  BarChart3,
  LineChart,
  BookOpen,
  Building2,
  Building,
  Camera,
  Video,
  LogOut,
  ChevronRight,
  ChevronDown,
  ScanFace,
  Clock,
  Briefcase,
  Locate,
  MapPinned,
  MapPin,
  CalendarClock,
  Gauge,
  ClipboardList,
  MessageSquare,
  BotMessageSquare,
  Layers,
  GitCompare,
  Activity,
  Inbox,
  Command,
  Cpu,
  Lock,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import logo from '../assets/images/logo.png';
import { NotificationBell } from '../components/notifications/NotificationBell';
import { usePendingReviewCount } from '../hooks/usePendingReviews';
import { useSectionReviewCount } from '../hooks/useSectionReview';
import { useSopProposalsPendingCount } from '../hooks/useSopProposals';
import { useRoleLevel } from '../lib/hooks/useRoleLevel';
// Phase 30 Wave 1 — deep import bypasses the barrel `@guild/config` re-export,
// which transitively pulls Node-only `process.env` reads (other config files)
// that fail tsc under the web app's `"types": ["vite/client"]` setup.
// The nav-visibility config file is intentionally pure-data; safe to import here.
// Path matches the package.json `exports: { './*': './src/*' }` rule.
import { NAV_VISIBILITY_CONFIG, type NavRouteKey } from '@guild/config/nav-visibility.config.js';
// Phase 36.2 — same deep-import contract as the line above. `ask.config.ts` is
// deliberately import-free and `process.env`-free so both the API and this file
// can read ONE constant; a flag the API took from an env var and the web took
// from a constant would be two sources that can disagree.
import { ASK_CONFIG } from '@guild/config/ask.config.js';
import { AskBar } from '../components/ask/AskBar';

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  badge?: 'pending-reviews' | 'section-reviews' | 'sop-proposals';
  children?: NavItem[];
  minLevel?: number; // minimum role level to see this item
  // Phase 30 Wave 1 (30.1.3) — TS-checked union derived from
  // `keyof typeof NAV_VISIBILITY_CONFIG`. A typo at the call site is a
  // compile-time error, not a silent mis-hide.
  routeKey?: NavRouteKey;
}

// PARAMETRIC-DISPATCH: NAV_VISIBILITY_CONFIG[<key>].defaultVisible + VITE_HIDE_NAV_<KEY>
// Composes static default with build-time env override. `=== 'true'` is mandatory:
// `import.meta.env[k]` returns `string | undefined`, never boolean. `=1`/`=yes`/etc.
// do NOT hide the item; only the literal string `'true'` does.
function isNavItemVisible(routeKey: NavRouteKey): boolean {
  const envKey = `VITE_HIDE_NAV_${routeKey.toUpperCase().replace(/[.-]/g, '_')}`;
  const hidden = import.meta.env[envKey] === 'true';
  return !hidden && (NAV_VISIBILITY_CONFIG[routeKey]?.defaultVisible ?? true);
}

// PARAMETRIC-DISPATCH: NAV_VISIBILITY_CONFIG[<key>].locked
// Phase 30 Wave 1 (30.1.2) — drives the small lock-icon badge rendered next to
// the label of locked nav items (e.g., Command Center, Machine Fleet). The
// underlying route still resolves to LockedFeaturePage via the route table; the
// badge is purely a sidebar affordance hint that the destination is a locked
// "upgrade" placeholder.
//
// `as const satisfies` in the config narrows each entry to its actual keys, so
// `entry.locked` is a TS error on entries that don't declare it. Using the `in`
// operator keeps the runtime branch type-safe.
function isNavItemLocked(routeKey: NavRouteKey): boolean {
  const entry = NAV_VISIBILITY_CONFIG[routeKey];
  return entry !== undefined && 'locked' in entry && entry.locked === true;
}

const navigation: NavItem[] = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
    children: [
      { name: 'Command Center', href: '/dashboard/command-center', icon: Command, routeKey: 'dashboard.command-center' },
    ],
  },
  { name: 'Operators', href: '/operators', icon: Users },
  {
    name: 'Library',
    href: '/sops',
    icon: FileText,
    minLevel: 4,
    children: [
      { name: 'Documents', href: '/sops', icon: FileText },
      { name: 'Proposals', href: '/sops/proposals', icon: Inbox, badge: 'sop-proposals' as const, minLevel: 3 },
      { name: 'KPIs', href: '/kpis', icon: Gauge },
      { name: 'Training', href: '/learning', icon: BookOpen },
      { name: 'Machine Fleet', href: '/sops/machine-fleet', icon: Cpu, routeKey: 'library.machine-fleet' },
    ],
  },
  {
    name: 'Assessments',
    href: '/assessments',
    icon: CheckSquare,
    children: [
      { name: 'Pending Review', href: '/assessments/pending-review', icon: ScanFace, badge: 'pending-reviews' },
      { name: 'Job Review', href: '/assessments/section-review', icon: Layers, badge: 'section-reviews' as const, minLevel: 4 },
    ],
  },
  {
    name: 'Analytics',
    href: '/analytics',
    icon: LineChart,
    children: [
      { name: 'Shift Comparison', href: '/analytics/shift-comparison', icon: GitCompare, minLevel: 3 },
      { name: 'Living SOPs', href: '/analytics/living-sops', icon: FileText, minLevel: 3 },
      { name: 'Ring 2 Effectiveness', href: '/analytics/ring-2-effectiveness', icon: Activity, minLevel: 5, routeKey: 'analytics.ring-2-effectiveness' },
      { name: 'Skill Matrix', href: '/skill-matrix', icon: BarChart3, routeKey: 'analytics.skill-matrix' },
      { name: 'KPI Analytics', href: '/analytics/kpi', icon: Gauge },
      { name: 'Operator Feedback', href: '/analytics/operator-feedback', icon: MessageSquare },
      { name: 'Coach Effectiveness', href: '/analytics/coach-effectiveness', icon: BotMessageSquare },
    ],
  },
  { name: 'Templates', href: '/settings/schedules', icon: CalendarClock },
];

const settingsNavigation: NavItem[] = [
  {
    name: 'Organization',
    href: '/settings/departments',
    icon: Building,
    minLevel: 4,
    children: [
      { name: 'Departments', href: '/settings/departments', icon: Building2 },
      { name: 'Shifts', href: '/settings/shifts', icon: Clock },
      { name: 'Positions', href: '/settings/positions', icon: Briefcase },
      { name: 'Users', href: '/settings/users', icon: Users },
      { name: 'Team', href: '/settings/team', icon: UserPlus },
    ],
  },
  {
    name: 'Infrastructure',
    href: '/settings/stations',
    icon: MapPin,
    minLevel: 4,
    children: [
      { name: 'Zones', href: '/settings/stations', icon: Locate },
      { name: 'Stations', href: '/settings/zones', icon: MapPinned },
      { name: 'Cameras', href: '/settings/cameras', icon: Camera },
      { name: 'Video Logs', href: '/settings/video-logs', icon: Video },
      { name: 'Checklists', href: '/settings/checklists', icon: ClipboardList },
    ],
  },
  // Phase 30 Wave 4 (30.4.1) — super_admin-only platform group. Children
  // navigate to placeholder routes that don't exist until chunks 30.4.2
  // (CameraRegistryPage) and 30.4.3b (OrganizationsPage) land; clicks 404
  // until then. The group's href and children all share the unique
  // `/settings/platform/` prefix, so `deriveActiveGroup` cleanly identifies
  // it as the active group without colliding with Organization or
  // Infrastructure. `minLevel: 6` gates the entire group to super_admin.
  {
    name: 'Platform',
    href: '/settings/platform/camera-registry',
    icon: Cpu,
    minLevel: 6,
    children: [
      { name: 'Camera Registry', href: '/settings/platform/camera-registry', icon: Camera },
      { name: 'Organizations', href: '/settings/platform/organizations', icon: Building2 },
    ],
  },
];

/**
 * Phase 30.1.1 — Find the parent group whose subtree (or own href) contains
 * the current pathname. Returns `null` for top-level routes (e.g., /dashboard,
 * /operators) that aren't under any expandable group, so all groups collapse.
 *
 * Operates on the FILTERED nav: a hidden or role-gated group is never
 * considered the "active" group even if the user is on its URL — the entry
 * isn't in `items` to begin with.
 */
function deriveActiveGroup(items: NavItem[], pathname: string): string | null {
  for (const item of items) {
    if (!item.children || item.children.length === 0) continue;
    // Child match wins first (deep-link case): /assessments/section-review
    // matches the Pending Review / Job Review children of Assessments.
    if (item.children.some(child =>
      pathname === child.href || pathname.startsWith(child.href + '/')
    )) {
      return item.name;
    }
    // Parent landing page (e.g., /assessments itself) also keeps the group open.
    if (pathname === item.href || pathname.startsWith(item.href + '/')) {
      return item.name;
    }
  }
  return null;
}

export function DashboardLayout() {
  // useAuth0 still used here for `user.name`/`user.email` (display) and logout.
  const { user, logout } = useAuth0();
  const location = useLocation();
  const { data: pendingReviewCount } = usePendingReviewCount();

  // Phase 19.S frontend migration (May 6, 2026) — role flows from
  // DB-canonical /api/me via useRoleLevel, not the JWT claim. Sidebar nav
  // items will re-render automatically when an admin updates a user's role
  // via /settings/users (the mutation invalidates /api/me).
  const { level: roleLevel } = useRoleLevel();

  const { data: sectionReviewCount } = useSectionReviewCount(roleLevel);
  const { data: sopProposalsCount } = useSopProposalsPendingCount(roleLevel);

  // Filter navigation items by visibility flag first (Phase 30 Wave 1 — Rule 2
  // dispatch site), then by role level. A hidden flag wins regardless of role
  // — the two filters compose AND-style, so call order doesn't change the
  // result; visibility-first lets the role pass operate on the smaller set.
  const filterNav = (items: NavItem[]): NavItem[] =>
    items
      .filter(item => !item.routeKey || isNavItemVisible(item.routeKey))
      .filter(item => !item.minLevel || roleLevel >= item.minLevel)
      .map(item => ({
        ...item,
        children: item.children
          ?.filter(child => !child.routeKey || isNavItemVisible(child.routeKey))
          .filter(child => !child.minLevel || roleLevel >= child.minLevel),
      }));

  const filteredNavigation = filterNav(navigation);
  const filteredSettings = filterNav(settingsNavigation);

  // Phase 30.1.1 — auto-expand the active group, collapse everything else.
  // Single derived value from useLocation().pathname (no useState, no
  // useEffect, no manual toggle). The active group is open because that's
  // where the user is — clicking the parent label/chevron on a non-active
  // group navigates to its landing href, which re-derives via this useMemo.
  // Clicking the chevron on the active group is a no-op (the underlying
  // <Link> navigation runs unchanged; the group stays open). Operates on the
  // FILTERED nav so a hidden group is never the "active" group even if the
  // user is on its URL.
  const activeGroup = useMemo<string | null>(() => {
    return deriveActiveGroup([...filteredNavigation, ...filteredSettings], location.pathname);
  }, [filteredNavigation, filteredSettings, location.pathname]);
  const isExpanded = (itemName: string): boolean => activeGroup === itemName;

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const isStaging = import.meta.env.VITE_ENVIRONMENT === 'staging';

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {isStaging && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-white text-center text-sm py-1 font-medium">
          STAGING ENVIRONMENT — Data here is not production
        </div>
      )}
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-30 w-[264px] flex flex-col bg-white border-r border-gray-100 shadow-sm ${isStaging ? 'pt-8' : ''}`}>
        {/* Logo Section */}
        <div className="flex h-16 items-center justify-center px-6 border-b border-gray-100">
          <Link
            to="/dashboard"
            className="group"
          >
            <div className="relative">
              <div className="absolute -inset-2 bg-guild-mint/30 rounded-xl blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <img
                src={logo}
                alt="Guild Insights"
                className="relative h-14 w-auto object-contain"
              />
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-6 px-4">
          <div className="space-y-1">
            {filteredNavigation.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const expanded = isExpanded(item.name);
              // For parent items, check if any child is active
              const isChildActive = hasChildren && item.children?.some(
                child => location.pathname === child.href || location.pathname.startsWith(child.href + '/')
              );
              const isActive = location.pathname === item.href || isChildActive;
              const Icon = item.icon;

              // Items WITH children: render as expandable section
              if (hasChildren) {
                return (
                  <div key={item.name}>
                    {/* Parent link — navigates to item.href, which re-derives
                        activeGroup via useLocation() and opens this group.
                        On the active group, the click is effectively a no-op
                        (already expanded; <Link> just re-navigates to the
                        same href). M1: no manual toggle. */}
                    <Link
                      to={item.href}
                      className={`
                        group relative flex items-center gap-3 rounded-xl px-4 py-3 w-full
                        text-sm font-semibold tracking-wide
                        transition-all duration-200 ease-out
                        ${isActive
                          ? 'bg-guild-green text-white shadow-lg shadow-guild-green/25'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }
                      `}
                    >
                      {isActive && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-guild-mint rounded-r-full" />
                      )}

                      <Icon
                        size={20}
                        strokeWidth={isActive ? 2.5 : 2}
                        className={`
                          flex-shrink-0 transition-transform duration-200
                          ${!isActive && 'group-hover:scale-110'}
                        `}
                      />

                      <span className="flex-1 text-left">{item.name}</span>

                      <ChevronDown
                        size={16}
                        className={`
                          transition-transform duration-200
                          ${expanded ? 'rotate-180' : ''}
                          ${isActive ? 'opacity-80' : 'opacity-50 group-hover:opacity-70'}
                        `}
                      />
                    </Link>

                    {/* Children (collapsible) */}
                    <div
                      className={`
                        overflow-hidden transition-all duration-200 ease-out
                        ${expanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
                      `}
                    >
                      <div className="mt-1 space-y-1">
                        {item.children?.map((child) => {
                          const childIsActive = location.pathname === child.href ||
                            location.pathname.startsWith(child.href + '/');
                          const ChildIcon = child.icon;
                          // Drives the lock badge for child items
                          // (e.g., Library → Machine Fleet).
                          const childIsLocked = child.routeKey ? isNavItemLocked(child.routeKey) : false;

                          return (
                            <Link
                              key={child.name}
                              to={child.href}
                              className={`
                                group relative flex items-center gap-3 rounded-xl pl-10 pr-4 py-2.5
                                text-sm font-medium tracking-wide
                                transition-all duration-200 ease-out
                                ${childIsActive
                                  ? 'bg-guild-green/10 text-guild-green'
                                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                                }
                              `}
                            >
                              <ChildIcon
                                size={18}
                                strokeWidth={childIsActive ? 2.5 : 2}
                                className="flex-shrink-0"
                              />

                              <span className="flex-1 whitespace-nowrap">{child.name}</span>

                              {/* Badge for pending reviews */}
                              {child.badge === 'pending-reviews' && pendingReviewCount && pendingReviewCount > 0 && (
                                <span className={`
                                  flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5
                                  text-[10px] font-bold tabular-nums
                                  ${childIsActive
                                    ? 'bg-guild-green text-white'
                                    : 'bg-amber-500 text-white'
                                  }
                                `}>
                                  {pendingReviewCount > 99 ? '99+' : pendingReviewCount}
                                </span>
                              )}

                              {/* Badge for section reviews */}
                              {child.badge === 'section-reviews' && sectionReviewCount && sectionReviewCount > 0 && (
                                <span className={`
                                  flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5
                                  text-[10px] font-bold tabular-nums
                                  ${childIsActive
                                    ? 'bg-guild-green text-white'
                                    : 'bg-amber-100 text-amber-700'
                                  }
                                `}>
                                  {sectionReviewCount > 99 ? '99+' : sectionReviewCount}
                                </span>
                              )}

                              {/* Badge for SOP proposals (Phase 23.2.1) */}
                              {child.badge === 'sop-proposals' && sopProposalsCount && sopProposalsCount > 0 && (
                                <span className={`
                                  flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5
                                  text-[10px] font-bold tabular-nums
                                  ${childIsActive
                                    ? 'bg-guild-green text-white'
                                    : 'bg-guild-mint/60 text-guild-green'
                                  }
                                `}>
                                  {sopProposalsCount > 99 ? '99+' : sopProposalsCount}
                                </span>
                              )}

                              {/* Phase 30 Wave 1 (30.1.2) — lock badge for child
                                  items with `routeKey.locked === true`
                                  (e.g., Machine Fleet). Persistent. Renders
                                  alongside any data badge above; for items
                                  with no data badge, replaces the hover chevron. */}
                              {childIsLocked && (
                                <span
                                  className={`
                                    flex h-5 w-5 items-center justify-center rounded-full
                                    ${childIsActive ? 'bg-guild-green/20' : 'bg-amber-100'}
                                  `}
                                  title="Locked — coming soon"
                                >
                                  <Lock
                                    size={11}
                                    strokeWidth={2.5}
                                    className={childIsActive ? 'text-guild-green' : 'text-amber-700'}
                                  />
                                </span>
                              )}

                              {!child.badge && !childIsLocked && !childIsActive && (
                                <ChevronRight
                                  size={14}
                                  className="opacity-0 -translate-x-2 group-hover:opacity-50 group-hover:translate-x-0 transition-all duration-200"
                                />
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              }

              // Items WITHOUT children: render as simple link
              const itemIsLocked = item.routeKey ? isNavItemLocked(item.routeKey) : false;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`
                    group relative flex items-center gap-3 rounded-xl px-4 py-3
                    text-sm font-semibold tracking-wide
                    transition-all duration-200 ease-out
                    ${isActive
                      ? 'bg-guild-green text-white shadow-lg shadow-guild-green/25'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }
                  `}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-guild-mint rounded-r-full" />
                  )}

                  <Icon
                    size={20}
                    strokeWidth={isActive ? 2.5 : 2}
                    className={`
                      flex-shrink-0 transition-transform duration-200
                      ${!isActive && 'group-hover:scale-110'}
                    `}
                  />

                  <span className="flex-1">{item.name}</span>

                  {/* Lock badge for `locked: true` items (e.g., Command Center).
                      Replaces the chevron-on-hover when present. Persistent. */}
                  {itemIsLocked ? (
                    <span
                      className={`
                        flex h-5 w-5 items-center justify-center rounded-full
                        ${isActive ? 'bg-white/20' : 'bg-amber-100'}
                      `}
                      title="Locked — coming soon"
                    >
                      <Lock
                        size={11}
                        strokeWidth={2.5}
                        className={isActive ? 'text-white' : 'text-amber-700'}
                      />
                    </span>
                  ) : (
                    !isActive && (
                      <ChevronRight
                        size={16}
                        className="opacity-0 -translate-x-2 group-hover:opacity-50 group-hover:translate-x-0 transition-all duration-200"
                      />
                    )
                  )}
                </Link>
              );
            })}
          </div>

          {/* Settings Section — hidden if no items pass role filter */}
          {filteredSettings.length > 0 && (
          <div className="mt-8 pt-6 border-t border-gray-100">
            <p className="px-4 mb-3 text-[11px] font-bold text-gray-400 uppercase tracking-[0.15em]">
              Settings
            </p>
            <div className="space-y-1">
              {filteredSettings.map((item) => {
                const hasChildren = item.children && item.children.length > 0;
                const expanded = isExpanded(item.name);
                // For parent items, check if any child is active
                const isChildActive = hasChildren && item.children?.some(
                  child => location.pathname === child.href || location.pathname.startsWith(child.href + '/')
                );
                const isActive = location.pathname === item.href || isChildActive;
                const Icon = item.icon;

                // Settings items always have children now
                if (hasChildren) {
                  return (
                    <div key={item.name}>
                      {/* Parent link — navigates to item.href, which re-derives
                          activeGroup via useLocation() and opens this group.
                          Was a <button onClick={toggleExpand}> pre-Phase-30.1.1.
                          The Link form is what makes a non-active group click
                          re-derive expansion (M1: no manual toggle). */}
                      <Link
                        to={item.href}
                        className={`
                          group relative flex items-center gap-3 rounded-xl px-4 py-3 w-full
                          text-sm font-semibold tracking-wide
                          transition-all duration-200 ease-out
                          ${isActive
                            ? 'bg-guild-green text-white shadow-lg shadow-guild-green/25'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }
                        `}
                      >
                        {isActive && (
                          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-guild-mint rounded-r-full" />
                        )}

                        <Icon
                          size={20}
                          strokeWidth={isActive ? 2.5 : 2}
                          className={`
                            flex-shrink-0 transition-transform duration-200
                            ${!isActive && 'group-hover:scale-110'}
                          `}
                        />

                        <span className="flex-1 text-left">{item.name}</span>

                        <ChevronDown
                          size={16}
                          className={`
                            transition-transform duration-200
                            ${expanded ? 'rotate-180' : ''}
                            ${isActive ? 'opacity-80' : 'opacity-50 group-hover:opacity-70'}
                          `}
                        />
                      </Link>

                      {/* Children (collapsible) */}
                      <div
                        className={`
                          overflow-hidden transition-all duration-200 ease-out
                          ${expanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}
                        `}
                      >
                        <div className="mt-1 space-y-1">
                          {item.children?.map((child) => {
                            const childIsActive = location.pathname === child.href ||
                              location.pathname.startsWith(child.href + '/');
                            const ChildIcon = child.icon;

                            return (
                              <Link
                                key={child.name}
                                to={child.href}
                                className={`
                                  group relative flex items-center gap-3 rounded-xl pl-10 pr-4 py-2.5
                                  text-sm font-medium tracking-wide
                                  transition-all duration-200 ease-out
                                  ${childIsActive
                                    ? 'bg-guild-green/10 text-guild-green'
                                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                                  }
                                `}
                              >
                                <ChildIcon
                                  size={18}
                                  strokeWidth={childIsActive ? 2.5 : 2}
                                  className="flex-shrink-0"
                                />

                                <span className="flex-1 whitespace-nowrap">{child.name}</span>

                                {!childIsActive && (
                                  <ChevronRight
                                    size={14}
                                    className="opacity-0 -translate-x-2 group-hover:opacity-50 group-hover:translate-x-0 transition-all duration-200"
                                  />
                                )}
                              </Link>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                }

                // Fallback for items without children (shouldn't happen with current structure)
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`
                      group relative flex items-center gap-3 rounded-xl px-4 py-3
                      text-sm font-semibold tracking-wide
                      transition-all duration-200 ease-out
                      ${isActive
                        ? 'bg-guild-green text-white shadow-lg shadow-guild-green/25'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }
                    `}
                  >
                    {isActive && (
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-guild-mint rounded-r-full" />
                    )}
                    <Icon size={20} strokeWidth={isActive ? 2.5 : 2} className="flex-shrink-0" />
                    <span className="flex-1">{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
          )}
        </nav>

        {/* User Section */}
        <div className="border-t border-gray-100 p-4">
          <div className="rounded-xl bg-gradient-to-br from-gray-50 to-gray-100/50 p-4">
            <div className="flex items-center gap-3">
              {/* Avatar */}
              <div className="relative">
                <div className="h-11 w-11 rounded-full bg-gradient-to-br from-guild-green to-guild-green/80 flex items-center justify-center ring-2 ring-white shadow-md">
                  <span className="text-white font-bold text-sm">
                    {user?.name?.[0]?.toUpperCase() || 'U'}
                  </span>
                </div>
                {/* Online indicator */}
                <div className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-emerald-400 ring-2 ring-white" />
              </div>

              {/* User info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900 truncate">
                  {user?.name || 'User'}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user?.email}
                </p>
              </div>
            </div>

            {/* Sign out button */}
            <button
              onClick={handleLogout}
              className="
                mt-4 w-full flex items-center justify-center gap-2
                rounded-lg border border-gray-200 bg-white
                px-4 py-2.5 text-sm font-semibold text-gray-600
                hover:border-gray-300 hover:text-gray-900 hover:shadow-sm
                active:scale-[0.98]
                transition-all duration-150
              "
            >
              <LogOut size={16} strokeWidth={2.5} />
              Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 ml-[264px] min-w-0 ${isStaging ? 'pt-8' : ''}`}>
        {/* Top Header with the ask bar + Notifications */}
        <div className={`sticky ${isStaging ? 'top-8' : 'top-0'} z-40 flex h-16 items-center justify-between gap-4 px-8 bg-white/80 backdrop-blur-sm border-b border-gray-100`}>
          {/* PARAMETRIC-DISPATCH: ASK_CONFIG.ENABLED
              Disabled removes the surface entirely — the feature-level rollback
              lever, mirroring the API route guard that returns 400. The empty
              div keeps `justify-between` pinning the bell to the right. */}
          {ASK_CONFIG.ENABLED ? <AskBar /> : <div />}
          <NotificationBell />
        </div>

        {/* Page Content */}
        <div className="min-h-[calc(100vh-64px)] p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
